import { AnimatePresence, motion } from 'framer-motion';
import { Radio, RefreshCw, WifiOff } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useLiveStore } from '@/stores/live-store';

/**
 * The operator's confidence gauge: is the map still live? Green while events
 * flow, amber while socket.io retries, grey once we have given up.
 */
export function ConnectionPill({ className }: { className?: string }) {
  const connection = useLiveStore((s) => s.connection);
  const lastEventAt = useLiveStore((s) => s.lastEventAt);

  const config = {
    connected: { label: 'Live', tone: 'text-moving', Icon: Radio },
    connecting: { label: 'Connecting', tone: 'text-idle', Icon: RefreshCw },
    reconnecting: { label: 'Reconnecting', tone: 'text-idle', Icon: RefreshCw },
    offline: { label: 'Offline', tone: 'text-offline', Icon: WifiOff },
    idle: { label: 'Standby', tone: 'text-offline', Icon: WifiOff },
  }[connection];

  const spinning = connection === 'connecting' || connection === 'reconnecting';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'flex h-9 items-center gap-1.5 rounded-lg border px-2.5 text-[12px] font-semibold',
            config.tone,
            className,
          )}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={connection}
              initial={{ opacity: 0, y: -3 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 3 }}
              transition={{ duration: 0.15 }}
              className="flex items-center gap-1.5"
            >
              <config.Icon className={cn('size-3', spinning && 'animate-spin')} strokeWidth={2.2} />
              {config.label}
            </motion.span>
          </AnimatePresence>
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {lastEventAt
          ? `Last telemetry ${relativeTime(lastEventAt)}`
          : 'Waiting for the first telemetry packet'}
      </TooltipContent>
    </Tooltip>
  );
}
